import { Option } from '@sapphire/result';

import {
  Survey,
  SurveyKind,
  SurveyStatusInfo,
  SurveyWorkflowPage,
} from '@ps/survey-utils';
import { BuyerSettings } from '@ps/user-settings-data-access';

export const INSIGHT = {
  // Includes the flexibility (CPI) pricing it is priced on.
  FEASIBILITY: 'feasibility',
  SCORE: 'score',
  // Supplier allocations seed and blends.
  SUPPLIERS: 'suppliers',
  PURE_PRICE: 'pure-price',
  EPC_PLATFORM: 'epc-platform',
  EPC_ESTIMATE: 'epc-estimate',
  EPC_ACTUAL: 'epc-actual',
  SIMILAR_SURVEYS: 'similar-surveys',
  // The tags catalog and the survey's own linked tags.
  TAGS: 'tags',
  IMPROBABLE_QUOTAS: 'improbable-quotas',
  UNUSED_QUALIFICATION_NOTICE: 'unused-qualification-notice',
  // A plain save re-running the insights above.
  SAVE_REFRESH: 'save-refresh',
} as const;

export type Insight = (typeof INSIGHT)[keyof typeof INSIGHT];

// One fact about the survey an insight may depend on.
type SurveyCondition = 'any' | 'draft' | 'draft-children-drawer';

// What a page needs from the survey: one condition, or several that must all hold.
type SurveyConditions = SurveyCondition | readonly SurveyCondition[];

// Who is working on the survey. Inside the survey workflow it is always a
// buyer, or an operator impersonating one (see surveyWorkflowAccessGuard).
export type Viewer = 'buyer' | 'impersonated-buyer';

interface InsightRule {
  readonly pages: Partial<Record<SurveyWorkflowPage, SurveyConditions>>;
  // Left out = every viewer.
  readonly viewers?: readonly Viewer[];
  // Left out = no buyer setting needed.
  readonly buyerSetting?: keyof BuyerSettings;
}

// Everything a rule is checked against.
export interface InsightContext {
  readonly page: Option<SurveyWorkflowPage>;
  readonly survey: Option<Survey>;
  readonly viewer: Viewer;
  readonly buyerSettings: BuyerSettings;
}

// WHERE, for WHOM and behind WHICH buyer setting each insight may call its
// API. A page left out = never on that page. The launch page shows estimates
// only in the children drawer table: its rows load, and a row's own
// CPI/completes edit refreshes that row.
const INSIGHT_RULES: Record<Insight, InsightRule> = {
  [INSIGHT.FEASIBILITY]: {
    pages: { edit: 'draft', launch: 'draft-children-drawer' },
  },
  [INSIGHT.SCORE]: {
    pages: { edit: 'draft' },
    buyerSetting: 'enableRYGSurveyScore',
  },
  [INSIGHT.SUPPLIERS]: { pages: { edit: 'draft' } },
  [INSIGHT.PURE_PRICE]: { pages: { edit: 'draft' } },
  [INSIGHT.EPC_PLATFORM]: {
    pages: { create: 'any', edit: 'draft' },
    viewers: ['impersonated-buyer'],
    buyerSetting: 'enableTargetMargin',
  },
  [INSIGHT.EPC_ESTIMATE]: {
    pages: { create: 'any', edit: 'draft' },
    viewers: ['impersonated-buyer'],
    buyerSetting: 'enableTargetMargin',
  },
  [INSIGHT.EPC_ACTUAL]: {
    pages: { edit: 'draft' },
    viewers: ['impersonated-buyer'],
    buyerSetting: 'enableTargetMargin',
  },
  [INSIGHT.SIMILAR_SURVEYS]: {
    pages: { edit: 'draft' },
    buyerSetting: 'enableSimilarSurveys',
  },
  [INSIGHT.TAGS]: {
    pages: { create: 'any', edit: 'any' },
    buyerSetting: 'enableSurveyTags',
  },
  [INSIGHT.IMPROBABLE_QUOTAS]: { pages: { edit: 'any' } },
  [INSIGHT.UNUSED_QUALIFICATION_NOTICE]: { pages: { edit: 'draft' } },
  [INSIGHT.SAVE_REFRESH]: { pages: { edit: 'draft' } },
};

// WHAT each survey condition means. A missing survey (pre-draft, or a child
// whose load failed) is not launched, and has no children drawer.
const SURVEY_CONDITIONS: Record<
  SurveyCondition,
  (survey: Option<Survey>) => boolean
> = {
  any: () => true,
  draft: (survey) =>
    survey.mapOr(true, (target) => SurveyStatusInfo.isDraft(target)),
  'draft-children-drawer': (survey) =>
    SURVEY_CONDITIONS.draft(survey) && survey.mapOr(false, hasChildrenDrawer),
};

export function isInsightAllowed(
  insight: Insight,
  context: InsightContext,
): boolean {
  const rule = INSIGHT_RULES[insight];
  return (
    allowsViewer(rule, context) &&
    allowsBuyerSetting(rule, context) &&
    allowsPage(rule, context)
  );
}

// A rule behind a buyer setting can only be decided once the settings load.
export function readsBuyerSetting(insight: Insight): boolean {
  return Option.from(INSIGHT_RULES[insight].buyerSetting).isSome();
}

function allowsViewer(
  { viewers }: InsightRule,
  { viewer }: InsightContext,
): boolean {
  return Option.from(viewers).mapOr(true, (allowed) =>
    allowed.includes(viewer),
  );
}

function allowsBuyerSetting(
  { buyerSetting }: InsightRule,
  { buyerSettings }: InsightContext,
): boolean {
  return Option.from(buyerSetting).mapOr(
    true,
    (setting) => !!buyerSettings[setting],
  );
}

function allowsPage(
  { pages }: InsightRule,
  { page, survey }: InsightContext,
): boolean {
  return page
    .andThen((current) => Option.from(pages[current]))
    .mapOr(false, (conditions) =>
      [conditions]
        .flat()
        .every((condition) => SURVEY_CONDITIONS[condition](survey)),
    );
}

function hasChildrenDrawer(survey: Survey): boolean {
  return SurveyKind.isTraditionalMc(survey) || SurveyKind.isGroupMc(survey);
}
